import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, computed, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { Observable, catchError, filter, map, of, tap } from 'rxjs';

import { AuthSessionResponse, SessionInfo } from '@common/types';

/** Unauthenticated baseline: returned for guest sessions, 401s, and errors. */
const UNAUTHENTICATED: SessionInfo = {
  authenticated: false,
  roles: [],
};

/**
 * Cookie-session bootstrap (AK-476 WP-F2). Reads auth state from the API-owned
 * session via GET /api/auth/session instead of browser-held OIDC tokens.
 *
 * Per the API team's definitive contract (AK-474, 2026-06-25):
 * - The endpoint accepts the `aiwb_session` cookie OR a Bearer token, so it is
 *   safe to call in both the cookie and legacy-OIDC worlds.
 * - The body is snake_case: `{ user_id, entity_id, auth_method, mfa, roles,
 *   feature_permissions, expires_at }`. There is no sessionId.
 * - Unauthenticated is signalled EITHER as HTTP 401 (the deployed app-domain
 *   edge strips `/api`, so the backend canonically sees `/auth/session` → 401)
 *   OR as HTTP 200 with `roles: ['guest']`. We treat both as unauthenticated and
 *   derive auth from the body, never the status code.
 *
 * Additive during the dual-auth window: the OIDC path stays intact and is the
 * fallback when this resolves unauthenticated (handled by the bootstrap caller).
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly _session = signal<SessionInfo>(UNAUTHENTICATED);

  /**
   * Whether the session has been resolved at least once (a load()/resolveSession
   * has completed). Until then `session$` withholds the provisional UNAUTHENTICATED
   * default so consumers don't observe a spurious "logged out" state during
   * bootstrap. AK-536: this is what stops cookie mode from emitting false -> true
   * and firing a redundant auth-change reload (e.g. duplicate /api/modules fetches).
   */
  private readonly _resolved = signal(false);

  /** Current session snapshot (signal). */
  readonly session: Signal<SessionInfo> = this._session.asReadonly();

  /**
   * Session as an observable, for the existing observable-based auth API.
   * Gated on `_resolved` so the first emission is the REAL session (authenticated
   * or guest), never the pre-load default — matching OIDC mode, where auth state
   * is already settled before consumers subscribe.
   *
   * Implemented via a computed that is `null` until resolved: the null -> session
   * transition is always a reference change, so `toObservable` emits even when the
   * resolved session is the shared UNAUTHENTICATED constant (a guest bootstrap) —
   * which a plain `filter` over `_session` would silently swallow (Object.is dedup).
   */
  readonly session$: Observable<SessionInfo> = toObservable(
    computed(() => (this._resolved() ? this._session() : null)),
  ).pipe(filter((session): session is SessionInfo => session !== null));

  constructor(private httpClient: HttpClient) {}

  get isAuthenticated(): boolean {
    return this._session().authenticated;
  }

  /**
   * Read the current session from the `aiwb_session` cookie (GET). Resolves to the
   * unauthenticated baseline (never errors) for guest responses, 401s, or transport
   * errors. `withCredentials` carries the HttpOnly session cookie.
   */
  load(): Observable<SessionInfo> {
    return this.resolveSession(
      this.httpClient.get<AuthSessionResponse>('/api/auth/session', { withCredentials: true }),
    );
  }

  /**
   * AK-476 cross-domain handoff: exchange the one-time `sessionId` the broker
   * callback hands back (`/auth?sessionId=...`) for an `aiwb_session` cookie scoped
   * to THIS app origin — the fix for tenants spread across multiple domains behind
   * one auth server, where a cookie set at the auth domain can't reach the app.
   *
   * POST (per Ranjit, 2026-06-26) so the one-time id rides the request body, never
   * a URL/query (keeps it out of logs, history, Referer). Body field is `sessionId`
   * (camelCase — matches the AK-474 spec and the camelCase id in the callback URL).
   *
   * Resolves to whether the exchange succeeded (2xx). The response body is partial
   * (no `roles`), so the FULL session is read separately via load() once the cookie
   * is set — never derive auth state from this response.
   */
  establishSession(sessionId: string): Observable<boolean> {
    return this.httpClient
      .post('/api/auth/session', { sessionId }, { observe: 'response', withCredentials: true })
      .pipe(
        map((response) => response.ok),
        catchError(() => of(false)),
      );
  }

  /** Shared pipeline: normalise, never throw (→ unauthenticated), and store. */
  private resolveSession(request$: Observable<AuthSessionResponse>): Observable<SessionInfo> {
    return request$.pipe(
      map((response) => this.normalize(response)),
      catchError((error) => {
        // 401 is the documented unauthenticated signal; anything else is unexpected
        // but still treated as unauthenticated to keep bootstrap resilient.
        if (error?.status !== 401) {
          console.warn('[AuthSession] session load failed; treating as unauthenticated', error);
        }
        return of(UNAUTHENTICATED);
      }),
      tap((session) => {
        this._session.set(session);
        this._resolved.set(true);
      }),
    );
  }

  /**
   * Fetch a one-shot socket ticket (AK-476 WP-F5). Valid for 60s, single-use;
   * cookie-authenticated. Fetch a fresh one immediately before each (re)connect.
   * Resolves to null on any failure so the caller can decide how to degrade.
   */
  getSocketTicket(): Observable<string | null> {
    return this.httpClient.get<{ ticket: string }>('/api/auth/socket-ticket', { withCredentials: true }).pipe(
      map((response) => response?.ticket ?? null),
      tap((ticket) =>
        console.debug(ticket ? '[Socket] ticket allocated' : '[Socket] ticket allocation returned empty'),
      ),
      catchError((error) => {
        console.error('[Socket] socket-ticket fetch failed', error);
        return of(null);
      }),
    );
  }

  /** Clear local session state (e.g. on logout). Does not call the API. */
  clear(): void {
    this._session.set(UNAUTHENTICATED);
  }

  private normalize(response: AuthSessionResponse): SessionInfo {
    const roles = response?.roles ?? [];
    // Authenticated = a real principal (user_id present) that is NOT a guest.
    // The unauthenticated signals are 401 (handled in resolveSession) or
    // `{ roles: ['guest'] }`. A logged-in user may legitimately have an EMPTY
    // roles array (no special roles) — that is still authenticated.
    const authenticated = !!response?.user_id && !roles.includes('guest');
    if (!authenticated) {
      return UNAUTHENTICATED;
    }
    return {
      authenticated: true,
      userId: response.user_id,
      entityId: response.entity_id,
      roles,
      authMethod: response.auth_method,
      mfa: response.mfa,
      featurePermissions: response.feature_permissions,
      expiresAt: response.expires_at,
    };
  }
}
